/* eslint-disable react/no-unstable-nested-components */
import { palette } from "lib/miolo";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import FinancialValueThresholdColumnStyles from "components/financials/FinancialValueThresholdColumnStyles";
import getYear from "date-fns/getYear";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import classNames from "components/ui/classNames";
import { FormattedMessage, useIntl } from "react-intl";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { ScrollArea } from "components/ui/ScrollArea";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import useSelfie from "lib/hooks/useSelfie";
import UnlockPremiumContentCTA from "components/conversion/UnlockPremiumContentCTA";

interface BrazilianTotalCompensationsContentProps {
  isRenderPageDefault: boolean;
}

export default function BrazilianTotalCompensationsContent({
  isRenderPageDefault,
}: BrazilianTotalCompensationsContentProps) {
  const intl = useIntl();
  const viewer = useSelfie();

  const issuer = useBrazilianIssuer();

  const { yearly } = issuer.financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0;

  const viewerIsPremiumSubscriber = !!viewer && viewer.isPremium && !viewer.isTrialing;

  const actualCompensations = issuer.totalCompensations
    .reduce<
      {
        fiscalYear: number;

        boardOfDirectors: number;
        boardOfExecutiveOfficers: number;
        fiscalCouncil: number;
        totalCompensation: number;

        netRevenue: number;
        netIncome: number;
      }[]
    >((accumulator, compensation) => {
      if (compensation.totalCompensation === 0) {
        return accumulator;
      }

      const compensationFiscalYearEnd = getYear(parseDateYmd(compensation.fiscalYearEnd));

      if (isBank) {
        const financialStatementRow = yearlyBankFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.financialIntermediationRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isCompany) {
        const financialStatementRow = yearlyCompanyFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isInsurer) {
        const financialStatementRow = yearlyInsurerFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      return accumulator;
    }, [])
    .sort((a, b) => {
      return a.fiscalYear - b.fiscalYear;
    });

  const theadClassName = classNames("px-3 py-1 text-sm font-semibold");

  return (
    <div className="flex h-auto w-full flex-col space-y-2">
      <div className="h-full w-full">
        <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
          <div className="pb-4 lg:w-full lg:pb-0">
            <div className="border-collapse overflow-hidden rounded-md border">
              <table className={classNames("min-w-full")}>
                <thead>
                  <tr>
                    <th
                      className={classNames(theadClassName, "text-white")}
                      style={{
                        backgroundColor: palette.blueBayoux,
                        borderRight: `solid 1px ${palette.lynch}`,
                      }}
                    >
                      <FormattedMessage
                        defaultMessage="Ano"
                        id="ptn1Js"
                        description="Remuneração dos Administradores"
                      />
                    </th>

                    <th
                      className={theadClassName}
                      style={{
                        color: palette.blueBayoux,
                        backgroundColor: palette.catskillWhite,
                        borderRight: `solid 1px ${palette.mystic}`,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em reais", id: "vSWhDO" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Diretoria"
                            id="kGjrtV"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={theadClassName}
                      style={{
                        color: palette.blueBayoux,
                        backgroundColor: palette.catskillWhite,
                        borderRight: `solid 1px ${palette.mystic}`,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em reais", id: "vSWhDO" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Conselho de Administração"
                            id="+syG2y"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={theadClassName}
                      style={{
                        color: palette.blueBayoux,
                        backgroundColor: palette.catskillWhite,
                        borderRight: `solid 1px ${palette.mystic}`,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em reais", id: "vSWhDO" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Conselho Fiscal"
                            id="5atf4D"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={theadClassName}
                      style={{
                        color: palette.scienceBlue,
                        backgroundColor: palette.lilyWhite,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em reais", id: "vSWhDO" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Total"
                            id="KKoQ7p"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={classNames(theadClassName, "tracking-wide")}
                      style={{
                        color: palette.seaGreen,
                        backgroundColor: palette.grannyApple,
                        borderLeft: `solid 1px ${palette.fringyFlower}`,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em milhões", id: "UFUJaz" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Lucro Líquido"
                            id="ZWn5dZ"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={classNames(theadClassName, "tracking-wide")}
                      style={{
                        color: palette.luxorGold,
                        backgroundColor: palette.linen,
                        borderLeft: `solid 1px ${palette.chamois}`,
                      }}
                    >
                      <FormattedMessage
                        defaultMessage="% Lucro"
                        id="HiAJXj"
                        description="Remuneração dos Administradores"
                      />
                    </th>

                    <th
                      className={classNames(theadClassName, "tracking-wide")}
                      style={{
                        color: palette.seaGreen,
                        backgroundColor: palette.grannyApple,
                        borderLeft: `solid 1px ${palette.fringyFlower}`,
                      }}
                    >
                      <SimpleTextTooltip
                        side="bottom"
                        content={intl.formatMessage({ defaultMessage: "Escala: Em milhões", id: "UFUJaz" })}
                      >
                        <div className="w-full justify-center">
                          <FormattedMessage
                            defaultMessage="Receita"
                            id="bid7Xd"
                            description="Remuneração dos Administradores"
                          />
                        </div>
                      </SimpleTextTooltip>
                    </th>

                    <th
                      className={classNames(theadClassName, "tracking-wide")}
                      style={{
                        color: palette.luxorGold,
                        backgroundColor: palette.linen,
                        borderLeft: `solid 1px ${palette.chamois}`,
                      }}
                    >
                      <FormattedMessage
                        defaultMessage="% Receita"
                        id="0yvHj/"
                        description="Remuneração dos Administradores"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={classNames(
                    "[&>tr>td]:text-center",
                    "[&>tr>td:not(:first-of-type)]:border-x",
                    "[&>tr>td:not(:first-of-type)]:border-[#dbe2eb]",
                    "[&>tr:hover]:bg-slate-50",
                  )}
                >
                  {actualCompensations
                    .sort((a, b) => a.fiscalYear - b.fiscalYear)
                    .slice(isRenderPageDefault ? -12 : undefined)
                    .map((compensation) => {
                      const baseClassName =
                        "px-3 py-1.5 font-mono text-sm font-semibold border-t border-r border-slate-200";

                      return (
                        <tr
                          key={compensation.fiscalYear}
                          className="group divide-x divide-gray-200 dark:divide-slate-700"
                        >
                          <td
                            className={baseClassName}
                            style={{
                              backgroundColor: palette.blueBayoux,
                              color: palette.white,
                              borderTop: `solid 1px ${palette.lynch}`,
                              borderBottom: `solid 1px ${palette.lynch}`,
                              borderRight: `solid 1px ${palette.lynch}`,
                            }}
                          >
                            <span>{compensation.fiscalYear}</span>
                          </td>
                          <td
                            className={baseClassName}
                            style={{
                              color: palette.lynch,
                            }}
                          >
                            {numeral(compensation.boardOfExecutiveOfficers).format("0,0").replace(/,/g, ".")}
                          </td>
                          <td
                            className={baseClassName}
                            style={{
                              color: palette.lynch,
                            }}
                          >
                            {numeral(compensation.boardOfDirectors).format("0,0").replace(/,/g, ".")}
                          </td>
                          <td
                            className={baseClassName}
                            style={{
                              color: palette.lynch,
                            }}
                          >
                            {numeral(compensation.fiscalCouncil).format("0,0").replace(/,/g, ".")}
                          </td>
                          <td
                            className={baseClassName}
                            style={{
                              color: palette.scienceBlue,
                            }}
                          >
                            {numeral(compensation.totalCompensation).format("0,0").replace(/,/g, ".")}
                          </td>
                          <td
                            className={baseClassName}
                            style={{
                              color: compensation.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                              border: `solid 1px ${palette.fringyFlower}`,
                              borderRight: "none",
                              borderBottom: "none",
                            }}
                          >
                            {formatFinancialValue(compensation.netIncome)}
                          </td>
                          <FinancialValueThresholdColumnStyles
                            value={
                              compensation.netIncome === 0
                                ? 0
                                : Math.abs((compensation.totalCompensation / compensation.netIncome) * 100)
                            }
                            comparators={[
                              () => {
                                return compensation.netIncome < 0;
                              },
                              (value) => {
                                return value >= 10;
                              },
                              (value) => {
                                return value < 10 && value > 3;
                              },
                              (value) => {
                                return value <= 3;
                              },
                            ]}
                            styles={[
                              () => ({
                                color: palette.mojo,
                                backgroundColor: "#FCF1F1",
                              }),
                              () => ({
                                color: palette.mojo,
                                backgroundColor: "#FCF1F1",
                              }),
                              () => ({
                                color: palette.kumera,
                                backgroundColor: palette.butteryWhite,
                              }),
                              () => ({
                                color: palette.seaGreen,
                                backgroundColor: "#F1FFF3",
                              }),
                            ]}
                          >
                            {(ratio, style) => {
                              let formattedRatio = `${(ratio || 0).toFixed(2).replace(".", ",")}%`;

                              if ((ratio || 0) >= 10) {
                                formattedRatio = `${Math.round(ratio || 0)}%`;
                              }

                              if ((ratio || 0) < 0 || compensation.netIncome < 0) {
                                formattedRatio = "P";
                              }

                              return (
                                <td
                                  className={classNames(baseClassName, "font-medium")}
                                  style={{
                                    color: palette.luxorGold,
                                    border: `solid 1px ${palette.chamois}`,
                                    borderBottom: "none",
                                    borderRight: "none",
                                    ...style,
                                  }}
                                >
                                  {formattedRatio}
                                </td>
                              );
                            }}
                          </FinancialValueThresholdColumnStyles>
                          <td
                            className={baseClassName}
                            style={{
                              color: palette.fruitSalad,
                              border: `solid 1px ${palette.fringyFlower}`,
                              borderBottom: "none",
                            }}
                          >
                            {formatFinancialValue(compensation.netRevenue)}
                          </td>
                          <FinancialValueThresholdColumnStyles
                            value={
                              compensation.netRevenue === 0
                                ? 0
                                : Math.abs((compensation.totalCompensation / compensation.netRevenue) * 100)
                            }
                            comparators={[
                              (value) => value >= 2,
                              (value) => value < 2 && value > 0.5,
                              (value) => value <= 0.5,
                            ]}
                            styles={[
                              () => ({
                                color: palette.mojo,
                                backgroundColor: "#FCF1F1",
                              }),
                              () => ({
                                color: palette.kumera,
                                backgroundColor: palette.butteryWhite,
                              }),
                              () => ({
                                color: palette.seaGreen,
                                backgroundColor: "#F1FFF3",
                              }),
                            ]}
                          >
                            {(ratio, style) => (
                              <td
                                className={classNames(baseClassName, "font-medium")}
                                style={{
                                  color: palette.luxorGold,
                                  border: `solid 1px ${palette.chamois}`,
                                  borderRight: "none",
                                  borderBottom: "none",
                                  ...style,
                                }}
                              >
                                {(ratio || 0) >= 10
                                  ? Math.round(ratio || 0)
                                  : (ratio || 0).toFixed(2).replace(".", ",")}
                                %
                              </td>
                            )}
                          </FinancialValueThresholdColumnStyles>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollArea>
      </div>

      {!viewerIsPremiumSubscriber && <UnlockPremiumContentCTA origin="Brazilian Compensations" />}
    </div>
  );
}
